import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Textarea,
  Button,
  RadioGroup,
  Radio,
  Stack,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Input,
  IconButton,
  List,
  ListItem,
  ListIcon,
  Badge,
  Divider,
  useColorModeValue,
  Spinner,
  Flex,
  Card,
  CardBody,
  CardHeader,
  Heading,
  useToast
} from '@chakra-ui/react';
import { 
  AddIcon, 
  DeleteIcon, 
  CheckCircleIcon, 
  AttachmentIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@chakra-ui/icons';
import { useDropzone } from 'react-dropzone';
import { useGlobalAction, useEnqueue } from '@gadgetinc/react';
import { api } from '../api';

const DocumentProcessorConfigurator = () => {
  const navigate = useNavigate();
  const toast = useToast();
  
  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [processingGoal, setProcessingGoal] = useState('');
  const [fieldDefinitionMethod, setFieldDefinitionMethod] = useState('manual');
  const [detectedFields, setDetectedFields] = useState([]);
  const [manualFieldName, setManualFieldName] = useState('');
  const [referenceFiles, setReferenceFiles] = useState([]);
  const [templateFile, setTemplateFile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [configName, setConfigName] = useState('');

  // Backend hooks
  const [{ data: configData, fetching: creatingConfig, error: configError }, createConfig] = useGlobalAction(api.documentProcessorConfig?.create || (() => {}));
  const [{ data: vectorStoreData, fetching: creatingVectorStore }, enqueueVectorStore] = useEnqueue(api.createVectorStoreWithFiles || api.createLocalVectorStore);

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  // Step validation
  const isStepValid = useCallback(() => {
    switch (currentStep) {
      case 1:
        return processingGoal.trim().length > 10;
      case 2:
        return detectedFields.length > 0;
      case 3:
        return true; // Reference files are optional
      default:
        return false;
    }
  }, [currentStep, processingGoal, detectedFields]);

  // File upload for template/sample data
  const onTemplateFileDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setTemplateFile(file);
      setIsAnalyzing(true);
      
      try {
        // Simulate field detection - in real implementation, this would parse the file
        setTimeout(() => {
          const mockFields = ['field1', 'field2', 'field3']; // This would come from file parsing
          setDetectedFields(mockFields.map(name => ({ name, id: Math.random().toString() })));
          setIsAnalyzing(false);
        }, 2000);
      } catch (error) {
        toast({
          title: 'File Analysis Failed',
          description: 'Unable to analyze the uploaded file. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
        setIsAnalyzing(false);
      }
    }
  }, [toast]);

  const templateDropzone = useDropzone({
    onDrop: onTemplateFileDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'text/csv': ['.csv'],
      'application/json': ['.json'],
      'image/*': ['.png', '.jpg', '.jpeg']
    },
    maxFiles: 1
  });

  // File upload for reference materials
  const onReferenceFileDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      id: Math.random().toString(),
      file,
      name: file.name,
      size: file.size
    }));
    setReferenceFiles(prev => [...prev, ...newFiles]);
  }, []);

  const referenceDropzone = useDropzone({
    onDrop: onReferenceFileDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'text/*': ['.txt', '.md'],
      'application/json': ['.json']
    },
    multiple: true
  });

  // Manual field management
  const addManualField = () => {
    if (manualFieldName.trim()) {
      setDetectedFields(prev => [...prev, { 
        id: Math.random().toString(), 
        name: manualFieldName.trim() 
      }]);
      setManualFieldName('');
    }
  };

  const removeField = (fieldId) => {
    setDetectedFields(prev => prev.filter(field => field.id !== fieldId));
  };

  const removeReferenceFile = (fileId) => {
    setReferenceFiles(prev => prev.filter(file => file.id !== fileId));
  };

  // Navigation
  const nextStep = () => {
    if (isStepValid() && currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const previousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Save configuration
  const saveConfiguration = async () => {
    try {
      setIsAnalyzing(true);

      // Create vector store for reference files if any
      let vectorStoreId = null;
      if (referenceFiles.length > 0) {
        const vectorStoreResult = await enqueueVectorStore({
          name: `${configName || 'Document Processor'} Reference Files`,
          files: referenceFiles.map(f => f.file)
        });
        vectorStoreId = vectorStoreResult?.data?.id;
      }

      // Create the document processor configuration
      const configResult = await createConfig({
        name: configName || 'Custom Document Processor',
        description: { markdown: `Document processor for: ${processingGoal}` },
        processingGoal: { markdown: processingGoal },
        configId: `config_${Date.now()}`,
        fieldDefinitionMethod: fieldDefinitionMethod === 'template' ? 'template_upload' : 
                               fieldDefinitionMethod === 'sample' ? 'sample_data' : 'manual_entry',
        detectedFields: detectedFields.reduce((acc, field) => {
          acc[field.name] = { type: 'string', required: false };
          return acc;
        }, {}),
        openAiVectorStoreId: vectorStoreId || `vs_${Date.now()}`,
        generatedPrompt: { 
          markdown: `Process documents to extract: ${detectedFields.map(f => f.name).join(', ')}. Goal: ${processingGoal}` 
        },
        aiParameters: {
          model: 'gpt-4',
          temperature: 0.1,
          maxTokens: 2000
        },
        completionsEndpoint: 'pmpt_68a36902cc24819095fa43d98d5898970dafa1b2692c35d1',
        inputMethod: 'file_upload',
        institution: 'default', // This should come from user context
        accessLevel: 'user_only',
        isActive: true
      });

      toast({
        title: 'Configuration Created Successfully',
        description: 'Your document processor has been set up and is ready to use.',
        status: 'success',
        duration: 5000,
        isClosable: true
      });

      navigate('/document-processing');
    } catch (error) {
      console.error('Error saving configuration:', error);
      toast({
        title: 'Configuration Failed',
        description: `Failed to create document processor: ${error.message}`,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Step indicator component
  const StepIndicator = ({ step, isActive, isCompleted }) => (
    <Flex 
      align="center" 
      justify="center" 
      w="40px" 
      h="40px" 
      borderRadius="full" 
      bg={isCompleted ? 'green.500' : isActive ? 'blue.500' : 'gray.300'}
      color="white"
      fontWeight="bold"
    >
      {isCompleted ? <CheckCircleIcon /> : step}
    </Flex>
  );

  return (
    <Box bg={bgColor} minH="100vh" py={8}>
      <Container maxW="4xl">
        {/* Header */}
        <VStack spacing={8} align="stretch">
          <Card bg={cardBg}>
            <CardHeader>
              <Heading size="lg" textAlign="center">
                Document Processor Configurator
              </Heading>
            </CardHeader>
            <CardBody>
              {/* Step indicators */}
              <HStack justify="center" spacing={8} mb={8}>
                <VStack spacing={2}>
                  <StepIndicator 
                    step={1} 
                    isActive={currentStep === 1} 
                    isCompleted={currentStep > 1} 
                  />
                  <Text fontSize="sm" fontWeight={currentStep === 1 ? 'bold' : 'normal'}>
                    Processing Goal
                  </Text>
                </VStack>
                <VStack spacing={2}>
                  <StepIndicator 
                    step={2} 
                    isActive={currentStep === 2} 
                    isCompleted={currentStep > 2} 
                  />
                  <Text fontSize="sm" fontWeight={currentStep === 2 ? 'bold' : 'normal'}>
                    Field Definition
                  </Text>
                </VStack>
                <VStack spacing={2}>
                  <StepIndicator 
                    step={3} 
                    isActive={currentStep === 3} 
                    isCompleted={false} 
                  />
                  <Text fontSize="sm" fontWeight={currentStep === 3 ? 'bold' : 'normal'}>
                    Reference Materials
                  </Text>
                </VStack>
              </HStack>

              <Progress 
                value={(currentStep / 3) * 100} 
                colorScheme="blue" 
                mb={8} 
                borderRadius="full"
              />

              {/* Step 1: Processing Goal */}
              {currentStep === 1 && (
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Text fontSize="xl" fontWeight="bold" mb={4}>
                      What is your goal in processing these documents or data?
                    </Text>
                    <Text color="gray.600" mb={4}>
                      Describe your objective in natural language. Be specific about what information you want to extract or analyze.
                    </Text>
                  </Box>
                  
                  <Input
                    placeholder="Name your configuration (optional)"
                    value={configName}
                    onChange={(e) => setConfigName(e.target.value)}
                    mb={4}
                  />

                  <Textarea
                    placeholder="Example: Extract patient demographics from medical forms, analyze survey responses for key themes, process invoice data for accounting..."
                    value={processingGoal}
                    onChange={(e) => setProcessingGoal(e.target.value)}
                    minH="120px"
                    resize="vertical"
                  />

                  {processingGoal.length > 0 && (
                    <Alert status="info">
                      <AlertIcon />
                      <Box>
                        <AlertTitle>Processing Goal Set!</AlertTitle>
                        <AlertDescription>
                          Character count: {processingGoal.length}
                          {processingGoal.length < 10 && " (Minimum 10 characters required)"}
                        </AlertDescription>
                      </Box>
                    </Alert>
                  )}
                </VStack>
              )}

              {/* Step 2: Field Definition */}
              {currentStep === 2 && (
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Text fontSize="xl" fontWeight="bold" mb={4}>
                      How would you like to define the data fields?
                    </Text>
                  </Box>

                  <RadioGroup 
                    value={fieldDefinitionMethod} 
                    onChange={setFieldDefinitionMethod}
                  >
                    <Stack spacing={4}>
                      <Radio value="template">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold">Upload Template Document</Text>
                          <Text fontSize="sm" color="gray.600">
                            Upload a sample document/form to automatically detect fields
                          </Text>
                        </VStack>
                      </Radio>
                      <Radio value="sample">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold">Upload Sample Data</Text>
                          <Text fontSize="sm" color="gray.600">
                            Upload sample data files (CSV, JSON, etc.) for field detection
                          </Text>
                        </VStack>
                      </Radio>
                      <Radio value="manual">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold">Manual Parameters</Text>
                          <Text fontSize="sm" color="gray.600">
                            Manually define field names you want to extract
                          </Text>
                        </VStack>
                      </Radio>
                    </Stack>
                  </RadioGroup>

                  <Divider />

                  {/* File upload for template/sample */}
                  {(fieldDefinitionMethod === 'template' || fieldDefinitionMethod === 'sample') && (
                    <Box>
                      <Box
                        {...templateDropzone.getRootProps()}
                        p={8}
                        border="2px dashed"
                        borderColor={templateDropzone.isDragActive ? "blue.300" : "gray.300"}
                        borderRadius="lg"
                        textAlign="center"
                        cursor="pointer"
                        _hover={{ borderColor: "blue.400", bg: "blue.50" }}
                      >
                        <input {...templateDropzone.getInputProps()} />
                        <AttachmentIcon boxSize={8} color="gray.400" mb={4} />
                        <Text mb={2}>
                          {templateDropzone.isDragActive 
                            ? "Drop the file here..." 
                            : `Click to upload ${fieldDefinitionMethod === 'template' ? 'template document' : 'sample data file'}`}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Supports PDF, DOCX, CSV, JSON, and images
                        </Text>
                      </Box>

                      {templateFile && (
                        <Alert status="success" mt={4}>
                          <AlertIcon />
                          <AlertTitle>File uploaded: {templateFile.name}</AlertTitle>
                        </Alert>
                      )}

                      {isAnalyzing && (
                        <Alert status="info" mt={4}>
                          <Spinner size="sm" mr={2} />
                          <AlertTitle>Analyzing file...</AlertTitle>
                          <AlertDescription>
                            Detecting fields from your uploaded file.
                          </AlertDescription>
                        </Alert>
                      )}
                    </Box>
                  )}

                  {/* Manual field entry */}
                  {fieldDefinitionMethod === 'manual' && (
                    <Box>
                      <HStack spacing={3}>
                        <Input
                          placeholder="Enter field name (e.g., 'Patient Name', 'Date', 'Amount')"
                          value={manualFieldName}
                          onChange={(e) => setManualFieldName(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && addManualField()}
                        />
                        <IconButton
                          icon={<AddIcon />}
                          onClick={addManualField}
                          colorScheme="blue"
                          aria-label="Add field"
                        />
                      </HStack>
                    </Box>
                  )}

                  {/* Display detected/manual fields */}
                  {detectedFields.length > 0 && (
                    <Box>
                      <Text fontWeight="bold" mb={3}>
                        Fields to extract ({detectedFields.length}):
                      </Text>
                      <List spacing={2}>
                        {detectedFields.map((field) => (
                          <ListItem key={field.id}>
                            <HStack justify="space-between" align="center">
                              <HStack>
                                <ListIcon as={CheckCircleIcon} color="green.500" />
                                <Text>{field.name}</Text>
                              </HStack>
                              <IconButton
                                size="sm"
                                icon={<DeleteIcon />}
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => removeField(field.id)}
                                aria-label={`Remove ${field.name}`}
                              />
                            </HStack>
                          </ListItem>
                        ))}
                      </List>

                      <Alert status="success" mt={4}>
                        <AlertIcon />
                        <Box>
                          <AlertTitle>Fields Ready!</AlertTitle>
                          <AlertDescription>
                            Missing fields? You can add more manually or contact support.
                          </AlertDescription>
                        </Box>
                      </Alert>
                    </Box>
                  )}
                </VStack>
              )}

              {/* Step 3: Reference Materials */}
              {currentStep === 3 && (
                <VStack spacing={6} align="stretch">
                  <Box>
                    <Text fontSize="xl" fontWeight="bold" mb={4}>
                      Upload Reference Materials (Optional)
                    </Text>
                    <Text color="gray.600" mb={4}>
                      Upload any reference files that you want AI to reference when handling your documents/data
                    </Text>
                  </Box>

                  <Box
                    {...referenceDropzone.getRootProps()}
                    p={8}
                    border="2px dashed"
                    borderColor={referenceDropzone.isDragActive ? "blue.300" : "gray.300"}
                    borderRadius="lg"
                    textAlign="center"
                    cursor="pointer"
                    _hover={{ borderColor: "blue.400", bg: "blue.50" }}
                  >
                    <input {...referenceDropzone.getInputProps()} />
                    <AttachmentIcon boxSize={8} color="gray.400" mb={4} />
                    <Text mb={2}>
                      {referenceDropzone.isDragActive 
                        ? "Drop reference files here..." 
                        : "Click to upload reference documents"}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      Supports PDF, DOCX, TXT, MD, JSON files
                    </Text>
                  </Box>

                  {referenceFiles.length > 0 && (
                    <Box>
                      <Text fontWeight="bold" mb={3}>
                        Reference Files ({referenceFiles.length}):
                      </Text>
                      <List spacing={2}>
                        {referenceFiles.map((file) => (
                          <ListItem key={file.id}>
                            <HStack justify="space-between" align="center">
                              <HStack>
                                <ListIcon as={AttachmentIcon} color="blue.500" />
                                <VStack align="start" spacing={0}>
                                  <Text>{file.name}</Text>
                                  <Text fontSize="sm" color="gray.500">
                                    {(file.size / 1024).toFixed(1)} KB
                                  </Text>
                                </VStack>
                              </HStack>
                              <IconButton
                                size="sm"
                                icon={<DeleteIcon />}
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => removeReferenceFile(file.id)}
                                aria-label={`Remove ${file.name}`}
                              />
                            </HStack>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Configuration Preview */}
                  <Card bg="gray.50" _dark={{ bg: "gray.700" }}>
                    <CardHeader>
                      <Heading size="md">Configuration Preview</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack align="start" spacing={3}>
                        <HStack>
                          <Badge colorScheme="blue">Goal:</Badge>
                          <Text fontSize="sm">{processingGoal}</Text>
                        </HStack>
                        <HStack>
                          <Badge colorScheme="green">Fields:</Badge>
                          <Text fontSize="sm">{detectedFields.map(f => f.name).join(', ')}</Text>
                        </HStack>
                        <HStack>
                          <Badge colorScheme="purple">References:</Badge>
                          <Text fontSize="sm">{referenceFiles.length} file(s)</Text>
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>
                </VStack>
              )}

              {/* Navigation buttons */}
              <HStack justify="space-between" mt={8}>
                <Button
                  leftIcon={<ChevronLeftIcon />}
                  onClick={previousStep}
                  isDisabled={currentStep === 1}
                  variant="outline"
                >
                  Previous
                </Button>

                <HStack spacing={3}>
                  <Button
                    variant="outline"
                    onClick={() => {
                      toast({
                        title: 'Draft Saved',
                        description: 'Your progress has been saved locally.',
                        status: 'info',
                        duration: 3000,
                        isClosable: true
                      });
                    }}
                  >
                    Save Draft
                  </Button>

                  {currentStep < 3 ? (
                    <Button
                      rightIcon={<ChevronRightIcon />}
                      onClick={nextStep}
                      isDisabled={!isStepValid()}
                      colorScheme="blue"
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      onClick={saveConfiguration}
                      isLoading={isAnalyzing || creatingConfig || creatingVectorStore}
                      loadingText="Creating..."
                      colorScheme="green"
                      isDisabled={!isStepValid()}
                    >
                      Create Configuration
                    </Button>
                  )}
                </HStack>
              </HStack>

              {/* Error handling */}
              {configError && (
                <Alert status="error" mt={4}>
                  <AlertIcon />
                  <AlertTitle>Configuration Error:</AlertTitle>
                  <AlertDescription>{configError.message}</AlertDescription>
                </Alert>
              )}
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  );
};

export default DocumentProcessorConfigurator;